// Node object factory — creates canvas node objects from raw API data and resolves design inheritance

import FontUtils from '@/services/canvas/fonts/Fonts';
import createImage from '@/services/canvas/utils/CreateImage';
import { getShape } from '@/modules/Node';
import store from '@/store/store';
import { computed } from 'vue';

const { fontFamilyGetNameByUrl } = FontUtils();
const hostURL = import.meta.env.VITE_ROOT_HOST;

// --- Interfaces ---

interface LangContent {
	node_link_to: string | null;
	content: string | null;
	marginal_content_1: string | null;
	marginal_content_2: string | null;
	title: string | null;
	abstract: string | null;
	short_content: string | null;
	node_search_terms: string | null;
	[key: string]: string | null;
}

interface RawNodeContent {
	lang: Record<string, LangContent>;
}

interface RawNodeGeometry {
	shape: string | null;
	center: { x: number; y: number };
}

interface RawNodeVisibility {
	min_level: number;
	max_level: number;
	hidden: boolean;
}

interface RawNodeEditing {
	locked: boolean;
}

export interface RawNode {
	id: number;
	content: RawNodeContent;
	geometry: RawNodeGeometry;
	design: NodeDesign;
	visibility: RawNodeVisibility;
	editing: RawNodeEditing;
}

export interface NodeDesign {
	object_template: string | null;
	shape: string | null;
	bg_enabled: boolean | null;
	bg_color: string | null;
	border_enabled: boolean | null;
	border_color: string | null;
	border_style: string | null;
	border_width: number | null;
	font_color: string | null;
	font_size: number | null;
	font_weight: number | null;
	font_style: string | null;
	font_url: string | null;
	line_height: number | null;
	height: number | null;
	width: number | null;
	rotate: number | null;
	media: string | null;
	media_opacity: number | null;
	media_url: string | null;
	media_size: number | null;
	media_cropped: boolean | null;
	shadow_blur: number | null;
	shadow_color: string | null;
	shadow_enabled: boolean | null;
	shadow_x: number | null;
	shadow_y: number | null;
	text_align: string | null;
	text_decoration: string | null;
	title_position: string | null;
	[key: string]: string | number | boolean | null;
}

interface NodeDefaults {
	nodes: NodeDesign;
}

interface TemplatesMap {
	[templateId: string]: NodeDesign;
}

export interface ElementsDesign {
	templates: TemplatesMap;
	nodes: NodeDesign;
	defaults: NodeDefaults;
}

interface Font {
	id: number;
	family_name: string;
	_links: { self: string };
	weights: Record<string, number>;
	faces: Array<{ url: string; style: string; weight: number }>;
}

export type FontsMap = Record<string, Font> | Font[];

export interface CanvasNode {
	id: number;
	type: string;
	template: string | null;
	shape: string;
	edgesFrom: number[];
	edgesTo: number[];
	width: number;
	height: number;
	rotate: number | null;
	x: number;
	y: number;
	radius: number;
	surface: boolean | null;
	bgColor: string | null;
	borderColor: string | null;
	borderStyle: string | null;
	borderWidth: number | null;
	border: boolean | null;
	shadow: boolean | null;
	shadowX: number | null;
	shadowY: number | null;
	shadowBlur: number | null;
	shadowColor: string | null;
	title: string | null;
	titlePosition: string | null;
	textAlign: string | null;
	lineHeight: number | null;
	fontFamily: string;
	fontWeight: number | null;
	fontStyle: string | null;
	fontSize: number | null;
	fontColor: string | null;
	media: string | null;
	mediaURL: string | null;
	mediaCropEnabled: boolean | null;
	mediaSize: number;
	mediaOpacity: number | null;
	image: HTMLImageElement | null;
	gifAnimation: boolean;
	visibilityMin: number;
	visibilityMax: number;
	hidden: boolean;
	hiddenLocal: boolean;
	locked: boolean;
	connectSign: boolean;
	editingContent: string | null;
	editingAppearance: string | null;
	availableForConnection: boolean;
	contentBlocked: boolean;
	contentLinkBlocked: boolean;
}

type NodePropertyPair = Record<string, string | number | boolean | null>;

type DispatchFn = (action: string, payload: Record<string, unknown>, options?: { root: boolean }) => void;

// --- Private helpers ---

function checkSiblingsIsoValue(
	langObj: Record<string, LangContent>,
	languagesISO: string[],
	key: string,
): boolean {
	return languagesISO.map((id) => langObj[id][key]).some((value) => value);
}

function getValueFromTemplate(
	templateId: string,
	templates: TemplatesMap,
	global: NodeDesign,
	property: string,
): string | number | boolean | null {
	if (templateId === '0') return global[property];
	// A node may reference a template that is not in the map (e.g. a stale
	// object_template id). Fall back to null rather than crashing the whole
	// canvas render — the caller then resolves the value from the defaults.
	return templates[templateId]?.[property] ?? null;
}

function mappingInverted(mapping: Record<string, string>): Record<string, string> {
	const invertedMapping: Record<string, string> = {};
	for (const [key, value] of Object.entries(mapping)) {
		invertedMapping[value] = key;
	}
	return invertedMapping;
}

// --- Public exports ---

export const NODE_DESIGN_MAPPING: Record<string, string> = {
	surface: 'bg_enabled',
	bgColor: 'bg_color',
	border: 'border_enabled',
	borderColor: 'border_color',
	borderStyle: 'border_style',
	borderWidth: 'border_width',
	fontColor: 'font_color',
	fontSize: 'font_size',
	fontWeight: 'font_weight',
	fontStyle: 'font_style',
	fontFamily: 'font_url',
	lineHeight: 'line_height',
	height: 'height',
	width: 'width',
	rotate: 'rotate',
	media: 'media',
	mediaOpacity: 'media_opacity',
	mediaURL: 'media_url',
	mediaSize: 'media_size',
	mediaCropEnabled: 'media_cropped',
	shadowBlur: 'shadow_blur',
	shadowColor: 'shadow_color',
	shadow: 'shadow_enabled',
	shadowX: 'shadow_x',
	shadowY: 'shadow_y',
	shape: 'shape',
	textAlign: 'text_align',
	textDecoration: 'text_decoration',
	titlePosition: 'title_position',
	template: 'object_template',

	// todo remove from design
	visibilityMin: 'min_level',
	visibilityMax: 'max_level',
};

const NODE_CONTENT_MAPPING: Record<string, string> = {
	abstract: 'abstract',
	content: 'content',
	marginalContent1: 'marginal_content_1',
	marginalContent2: 'marginal_content_2',
	nodeLinkTo: 'node_link_to',
	nodeSearchTerms: 'node_search_terms',
	shortContent: 'short_content',
	title: 'title',
};

export function createNodeObject(
	nodeOriginal: RawNode,
	elementsDesign: ElementsDesign,
	fonts: FontsMap,
): CanvasNode {
	const currentLang = computed(() => store.getters['maps/languages/getLanguage'].id as string);
	const languagesChosen = computed(
		() => store.getters['maps/languages/getLanguagesChosenComputed'] as Array<{ id: string }>,
	);
	const gifAnimationStatus = computed(
		() => store.getters['nodes/design/getGifAnimationStatus'] as boolean,
	);
	const languagesISO = [currentLang.value, ...languagesChosen.value.map((item) => item.id)];

	const LINK = 'node_link_to';
	const CONTENT = 'content';
	const MARGINAL1 = 'marginal_content_1';
	const MARGINAL2 = 'marginal_content_2';
	const contentBlocked = checkSiblingsIsoValue(nodeOriginal.content.lang, languagesISO, LINK);
	const contentLinkBlocked =
		checkSiblingsIsoValue(nodeOriginal.content.lang, languagesISO, CONTENT) ||
		checkSiblingsIsoValue(nodeOriginal.content.lang, languagesISO, MARGINAL1) ||
		checkSiblingsIsoValue(nodeOriginal.content.lang, languagesISO, MARGINAL2);

	const nodeDesign = getNodeOriginalDesign(nodeOriginal, elementsDesign);
	if (!nodeDesign.shape) nodeDesign.shape = 'circle';
	if (
		getShape(nodeDesign.shape as string).resize.constrainDimensions &&
		nodeDesign.width !== nodeDesign.height
	) {
		nodeDesign.width = nodeDesign.height = Math.max(
			nodeDesign.width as number,
			nodeDesign.height as number,
		);
	}

	const node: CanvasNode = {
		id: nodeOriginal.id,
		type: nodeOriginal.geometry.shape || 'circle',
		template: nodeOriginal.design.object_template,
		shape: nodeDesign.shape as string,
		edgesFrom: [],
		edgesTo: [],
		width: nodeDesign.width as number,
		height: nodeDesign.height as number,
		rotate: nodeDesign.rotate as number | null,
		x: nodeOriginal.geometry.center.x,
		y: nodeOriginal.geometry.center.y,
		radius: (nodeDesign.width as number) / 2,
		surface: nodeDesign.bg_enabled as boolean | null,
		bgColor: nodeDesign.bg_color as string | null,
		borderColor: nodeDesign.border_color as string | null,
		borderStyle: nodeDesign.border_style as string | null,
		borderWidth: nodeDesign.border_width as number | null,
		border: nodeDesign.border_enabled as boolean | null,
		shadow: nodeDesign.shadow_enabled as boolean | null,
		shadowX: nodeDesign.shadow_x as number | null,
		shadowY: nodeDesign.shadow_y as number | null,
		shadowBlur: nodeDesign.shadow_blur as number | null,
		shadowColor: nodeDesign.shadow_color as string | null,
		title: nodeOriginal.content.lang[currentLang.value].title,
		titlePosition: nodeDesign.title_position as string | null,
		textAlign: nodeDesign.text_align as string | null,
		lineHeight: nodeDesign.line_height as number | null,
		fontFamily:
			fontFamilyGetNameByUrl(nodeDesign.font_url as string | null, fonts) ||
			(fonts as Font[])[0].family_name,
		fontWeight: nodeDesign.font_weight as number | null,
		fontStyle: nodeDesign.font_style as string | null,
		fontSize: nodeDesign.font_size as number | null,
		fontColor: nodeDesign.font_color as string | null,
		media: nodeDesign.media as string | null,
		mediaURL: nodeDesign.media_url as string | null,
		mediaCropEnabled: nodeDesign.media_cropped as boolean | null,
		mediaSize: (nodeDesign.media_size as number) || 100,
		mediaOpacity: nodeDesign.media_opacity as number | null,
		image: null,
		gifAnimation: gifAnimationStatus.value,
		visibilityMin: nodeOriginal.visibility.min_level,
		visibilityMax: nodeOriginal.visibility.max_level,
		hidden: nodeOriginal.visibility.hidden,
		hiddenLocal: false,
		locked: nodeOriginal.editing.locked,
		connectSign: false,
		editingContent: null,
		editingAppearance: null,
		availableForConnection: true,
		contentBlocked,
		contentLinkBlocked,
	};

	const media = node.media;
	const mediaURL = node.mediaURL;
	const computedMedia = media?.includes(hostURL as string) ? media : `${hostURL}${media}`;
	if (media) {
		node.image = createImage(computedMedia, null, null, nodeOriginal.id);
	}
	if (mediaURL) {
		node.image = createImage(mediaURL, null, null, nodeOriginal.id);
	}

	return node;
}

export function getNodeOriginalDesign(
	node: RawNode,
	elementsDesign: ElementsDesign,
): NodeDesign {
	const {
		templates,
		nodes: global,
		defaults: { nodes: nodeDefaults },
	} = elementsDesign;

	const nodeOriginalDesign: NodeDesign = {} as NodeDesign;
	const unlinkedDesign: NodeDesign = JSON.parse(JSON.stringify(node.design));

	// Iterate over ALL known keys (defaults has every property).
	// Minimal designs (e.g. {"object_template":"7"}) omit most keys —
	// treat missing keys the same as null so they resolve from template/global/defaults.
	const allKeys = new Set([...Object.keys(nodeDefaults), ...Object.keys(unlinkedDesign)]);
	for (const key of allKeys) {
		const value = key in unlinkedDesign ? unlinkedDesign[key] : null;
		if (value === null && key !== 'object_template') {
			const templateId = node.design.object_template;
			const templateValue =
				templateId !== null
					? getValueFromTemplate(templateId, templates, global, key)
					: null;
			const propertyValue =
				templateId !== null && templateValue != null && templateValue !== ''
					? templateValue
					: nodeDefaults[key];
			nodeOriginalDesign[key] = propertyValue;
		} else {
			nodeOriginalDesign[key] = value;
		}
	}

	// special check media fields
	if (unlinkedDesign.media && nodeOriginalDesign.media_url) {
		nodeOriginalDesign.media_url = null;
	}
	if (unlinkedDesign.media_url && nodeOriginalDesign.media) {
		nodeOriginalDesign.media = null;
	}

	return nodeOriginalDesign;
}

export function getNodeDesignFromTemplate(
	templateId: string | null,
	elementsDesign: ElementsDesign,
): NodeDesign {
	const {
		templates,
		nodes: global,
		defaults: { nodes: nodeDefaults },
	} = elementsDesign;

	const nodeDesign: NodeDesign = {} as NodeDesign;

	if (templateId !== null) {
		const template = templateId !== '0' ? templates[templateId] : global;
		// Iterate over ALL default keys to handle minimal templates/globals
		const allKeys = new Set([...Object.keys(nodeDefaults), ...Object.keys(template)]);
		for (const key of allKeys) {
			const value = key in template ? template[key] : null;
			if (value == null) {
				const globalValue = key in global ? global[key] : null;
				nodeDesign[key] = globalValue != null ? globalValue : nodeDefaults[key];
			} else {
				nodeDesign[key] = value;
			}
		}
	} else {
		return { ...nodeDefaults };
	}

	return nodeDesign;
}

export function getNodeDesignInitialComputedValue(
	templateId: string | null,
	elementDesign: ElementsDesign,
	property: string,
): string | number | boolean | null {
	const nodeDesign = getNodeDesignFromTemplate(templateId, elementDesign);
	return nodeDesign[property];
}

export function getNodeComputedDesign(
	nodeId: number,
	nodes: CanvasNode[],
	rawNodes: RawNode[],
	elementsDesign: ElementsDesign,
	fonts: FontsMap,
): Partial<CanvasNode> {
	const nodeOriginal = rawNodes.find((node) => node.id === nodeId) as RawNode;
	const node = nodes.find((n) => n.id === nodeId) as CanvasNode;
	const nodeDesign: Partial<CanvasNode> = {};
	const nodeOriginalDesign = getNodeOriginalDesign(nodeOriginal, elementsDesign);

	for (let [key, value] of Object.entries(nodeOriginalDesign)) {
		if (key === 'font_url' && value) {
			value = fontFamilyGetNameByUrl(value as string, fonts);
		}
		if (key === 'media' && value && !node.image?.src.includes(value as string)) {
			const imageUrl = (value as string).includes(hostURL as string)
				? (value as string)
				: `${hostURL}${value}`;
			Object.assign(nodeDesign, { image: createImage(imageUrl, null, null, nodeId) });
		}
		if (key === 'media_url' && value && !node.image?.src.includes(value as string)) {
			Object.assign(nodeDesign, {
				image: createImage(value as string, null, null, nodeId),
			});
		}
		if (key === 'media' && !value && !nodeOriginalDesign.media_url) {
			nodeDesign.image = null;
		}
		const converted = convertOriginalToNodeProperty({ [key]: value });
		if (converted) {
			Object.assign(nodeDesign, converted);
		}
	}

	if (
		getShape(nodeDesign.shape as string).resize.constrainDimensions &&
		nodeDesign.width !== nodeDesign.height
	) {
		nodeDesign.width = nodeDesign.height = Math.max(
			nodeDesign.width as number,
			nodeDesign.height as number,
		);
	}

	return nodeDesign;
}

export function getNodeChangedValues(
	node: Partial<NodeDesign>,
): Partial<NodeDesign> {
	const updatedValues: Partial<NodeDesign> = {};
	for (const [property, value] of Object.entries(node)) {
		if (property !== 'object_template' && value !== null) {
			updatedValues[property] = value;
		}
	}
	return updatedValues;
}

export function convertToOriginalNodeProperty(
	nodeProperty: NodePropertyPair,
): NodePropertyPair | undefined {
	const [entries] = Object.entries(nodeProperty);
	const [key, value] = entries;
	if (NODE_DESIGN_MAPPING[key]) {
		return { [NODE_DESIGN_MAPPING[key]]: value };
	}
	return undefined;
}

export function convertOriginalToNodeProperty(
	nodeProperty: NodePropertyPair,
): NodePropertyPair | undefined {
	const [[key, value]] = Object.entries(nodeProperty);
	const mapping = mappingInverted(NODE_DESIGN_MAPPING);
	if (mapping[key]) {
		return { [mapping[key]]: value };
	}
	return undefined;
}

export function adaptedNodeDesignProperties(
	node: NodeDesign,
): Record<string, string | number | boolean | null> {
	const adaptedOriginal: Record<string, string | number | boolean | null> = {};
	for (const [key, value] of Object.entries(NODE_DESIGN_MAPPING)) {
		if (node[value] !== undefined) {
			adaptedOriginal[key] = node[value];
		}
	}
	return adaptedOriginal;
}

export function edgeUpdateRelated(
	currentNode: CanvasNode,
	propertyFrom: string,
	propertyTo: string,
	value: number | string | boolean | null,
	dispatch: DispatchFn,
): void {
	if (currentNode.edgesFrom) {
		currentNode.edgesFrom.forEach((edge) => {
			const updatedData: Record<string, unknown> = {
				edgeId: edge,
				[propertyFrom]: value,
			};
			dispatch('edges/updateEdge', updatedData, { root: true });
		});
	}
	if (currentNode.edgesTo) {
		currentNode.edgesTo.forEach((edge) => {
			const updatedData: Record<string, unknown> = {
				edgeId: edge,
				[propertyTo]: value,
			};
			dispatch('edges/updateEdge', updatedData, { root: true });
		});
	}
}

export function convertContentToOriginalProperty(
	contentProperty: NodePropertyPair,
): NodePropertyPair {
	const [[key, value]] = Object.entries(contentProperty);
	return { [NODE_CONTENT_MAPPING[key]]: value };
}

export function convertContentToAdaptedProperty(
	contentProperty: NodePropertyPair,
): NodePropertyPair {
	const [[key, value]] = Object.entries(contentProperty);
	const mapping = mappingInverted(NODE_CONTENT_MAPPING);
	return { [mapping[key]]: value };
}

export function getNodeUpdatedData(
	node: Record<string, unknown>,
): Record<string, unknown> {
	const updatedValues: Record<string, unknown> = {};
	for (const [property, value] of Object.entries(node)) {
		if (property !== 'object_template' && value !== null) {
			updatedValues[property] = value;
		}
	}
	return updatedValues;
}
